import { useState } from 'react';
import { FlaskConical, X } from 'lucide-react';
import {
  RULE_CATEGORIES,
  SEVERITIES,
  useSimulateNotification,
  type RuleSeverity,
  type SimulateScope,
  type SimulatedMatch,
} from '@/hooks/useNotifications.ts';
import { SEVERITY_LABELS, isTagKind, type ScopeOption } from './ruleSentence.ts';
import { ScopePicker } from './ClauseEditor.tsx';
import { CHANNEL_LABELS } from './deliveryStatus.ts';
import { Badge } from '@/components/ui/badge.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Label } from '@/components/ui/label.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.tsx';
import { Switch } from '@/components/ui/switch.tsx';

/**
 * "Who would this notify?" — §9's simulator.
 *
 * It runs the **real planner** against an event that is never written, which is what
 * makes it worth having: a preview computed by a second implementation would agree
 * with the router right up until it mattered. That is also why the answer includes
 * the near-misses — a match that would be suppressed is the interesting case, and it
 * is the one a mental model gets wrong.
 *
 * It sits under the routes it explains. A route is written blind otherwise: the
 * clauses read as English but nothing tells you whether *this* organization's
 * divisions and severities actually produce a match.
 *
 * The response names destinations by **display name, never address** — the set of
 * people who may ask "would this page anyone?" is wider than the set who may read
 * where it would go.
 */

/** Scopes are a set on the event; the any/all reading belongs to the rule, not here. */
function scopeLabel(scope: SimulateScope, options: ScopeOption[]): string {
  const id = scope.divisionId ?? scope.regionId;
  const found = options.find((o) => o.id === id);
  const noun = scope.divisionId ? 'division' : 'region';
  return `${noun} ${found?.name ?? 'unknown'}`;
}

function MatchRow({ match }: { match: SimulatedMatch }) {
  const who =
    match.kind === 'route'
      ? (match.routeName ?? 'an organization route')
      : match.kind === 'forced'
        ? 'Transactional (always delivered)'
        : 'A personal rule';

  const channel = CHANNEL_LABELS[match.channel] ?? match.channel;

  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-border-subtle px-3 py-2">
      <div className="min-w-0">
        <p className="text-sm truncate">
          {who}
          {match.destinationName ? <span className="text-muted-foreground"> → {match.destinationName}</span> : null}
        </p>
        <p className="text-xs text-muted-foreground">
          {channel}
          {/* Why it would NOT deliver is the whole point of showing a non-match. */}
          {match.wouldDeliver ? '' : ` · ${match.suppressedBy ?? 'suppressed'}`}
          {match.detail ? ` · ${match.detail}` : ''}
        </p>
      </div>
      <Badge variant={match.wouldDeliver ? 'default' : 'outline'} className="font-normal shrink-0">
        {match.wouldDeliver ? 'Would deliver' : 'Would not'}
      </Badge>
    </div>
  );
}

export function RoutingSimulator({ scopes }: { scopes: ScopeOption[] }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('incidents');
  const [severity, setSeverity] = useState<RuleSeverity>('high');
  const [isResolution, setIsResolution] = useState(false);
  const [eventScopes, setEventScopes] = useState<SimulateScope[]>([]);
  const simulate = useSimulateNotification();

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => { setOpen(true); }}>
        <FlaskConical className="h-3.5 w-3.5 mr-1.5" /> Preview who gets notified
      </Button>
    );
  }

  const run = () => { simulate.mutate({ category, severity, isResolution, scopes: eventScopes }); };
  const result = simulate.data;

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Preview who gets notified
        </h4>
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setOpen(false); }} aria-label="Close preview">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Runs the real matcher against an event that is never recorded. Nothing is sent and nothing is stored.
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs">About</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RULE_CATEGORIES.map((c) => (
                <SelectItem key={c.key} value={c.key}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Severity</Label>
          <Select value={severity} onValueChange={(v) => { setSeverity(v as RuleSeverity); }}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SEVERITIES.map((s) => (
                <SelectItem key={s} value={s}>
                  {SEVERITY_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Affecting</Label>
        <div className="flex flex-wrap items-center gap-2">
          {eventScopes.map((scope, index) => (
            <span
              key={`${scope.divisionId ?? scope.regionId ?? ''}-${String(index)}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 pl-2.5 pr-1 py-1 text-xs"
            >
              {scopeLabel(scope, scopes)}
              <button
                type="button"
                className="text-muted-foreground hover:text-destructive p-0.5"
                onClick={() => { setEventScopes((s) => s.filter((_, i) => i !== index)); }}
                aria-label={`Remove ${scopeLabel(scope, scopes)}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <ScopePicker
            // An event happens in a division or a region; a tag is not a place, and
            // picking one used to add a scope with neither ("region unknown").
            scopes={scopes.filter((o) => !isTagKind(o.kind))}
            label="Add scope"
            onPick={(scope) => {
              setEventScopes((s) => [
                ...s,
                {
                  divisionId: scope.kind === 'division' ? scope.id : null,
                  regionId: scope.kind === 'region' ? scope.id : null,
                },
              ]);
            }}
          />
        </div>
        {/* Several scopes is the only way to exercise "confined to" against an incident
            that spans more than one — a single scope makes any/all indistinguishable. */}
        <p className="text-xs text-muted-foreground">
          Add more than one to test a spreading incident — that is where “affecting” and “confined to” differ.
        </p>
      </div>

      <label className="flex items-center gap-2 text-xs">
        <Switch checked={isResolution} onCheckedChange={setIsResolution} />
        Simulate the resolution rather than the opening
      </label>

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={run} disabled={simulate.isPending}>
          {simulate.isPending ? 'Checking…' : 'Check'}
        </Button>
        {simulate.isError ? <span className="text-xs text-destructive">Couldn&apos;t run the preview.</span> : null}
      </div>

      {result ? (
        <div className="space-y-2 border-t border-border pt-3">
          <p className="text-xs text-muted-foreground">
            {result.rulesMatched} of {result.rulesConsidered} personal {result.rulesConsidered === 1 ? 'rule' : 'rules'}
            {' · '}
            {result.routesMatched} of {result.routesConsidered} {result.routesConsidered === 1 ? 'route' : 'routes'}
          </p>

          {/* Not the same statement as "nothing else matched", and the API distinguishes
              them for exactly that reason. */}
          {result.filteredToSelf ? (
            <p className="text-xs text-muted-foreground">
              Showing only your own rules — seeing everyone the organization would notify needs permission to read
              its routes.
            </p>
          ) : null}

          {result.matches.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Nothing would be notified. An event like this reaches nobody.
            </p>
          ) : (
            <div className="space-y-2">
              {result.matches.map((m, i) => (
                <MatchRow key={`${m.kind}-${m.id ?? ''}-${String(i)}`} match={m} />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
